import { Box, Button, Center, Flex, Icon, Spinner } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@/ui/editor/extensions/link';
// import TextAlign from '@tiptap/extension-text-align';
import { EditorToolbar } from './EditorToolbar';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useField } from 'formik';
import { sanitizeDom } from '@/lib/purify';
import { NonEditableTag } from './NonEditableTag';
import { SanitizeTemplateVariables } from '@/utils/editor/sanitize-template-variables';
import { processSelectedContent } from './utils/processSelectedContent';
import EditorCustomVariables from './EditorCustomVariables';
import { debounce } from 'lodash-es';
import { SizedImage } from '@/components/editor/image-extension';
import { TextDirection } from '@/ui/editor/extensions/text-direction';
import { FileAttachmentPreview } from './FileAttachmentPreview';
import Placeholder from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { FontSize } from '@/ui/editor/extensions/font-size';
import { FontFamily } from '@tiptap/extension-font-family';
import { AutoSave } from '@/ui/autosave';
import { ValidateLinkOnPaste } from '@/ui/editor/extensions/validate-link';
import { useKFlags } from '@/hooks/use-k-flags';
import VariableHighlightDecoration from '@/ui/editor/extensions/variable-highlight-decoration';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { createLowlight } from 'lowlight';
import javascript from 'highlight.js/lib/languages/javascript';
import css from 'highlight.js/lib/languages/css';
import ts from 'highlight.js/lib/languages/typescript';
import html from 'highlight.js/lib/languages/xml';
import 'highlight.js/styles/github.css';
import './editor.css';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { EyeOff } from '@kula-ai/icons/outline/EyeOff';

import { SanitizedBox } from '@/ui/SanitizedDom';
import { getPlaceholderPreview } from './api';
import { useMutation } from '@tanstack/react-query';
import { EnterHandler } from '@/ui/editor/extensions/enter-handler';
import {
  DefinitionDescription,
  DefinitionList,
  DefinitionTerm,
  getTableExtensions,
  TableBubbleMenu,
  SurveyNode,
  SurveyLinkReplacement,
  VariableNode,
  VariableHydration,
} from './extensions';
import { serializeVariables } from '@/utils/variableFormatter';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';

export const FormEditor = ({ item = {}, formik }) => {
  const {
    name,
    pluginAlignment = 'top',
    editorContainerStyles = {},
    content = '',
    editable = true,
    replaceContent = false,
    contentType = 'html',
    renderAdditionalContent = () => {},
    editorWraperStyles = {},
    editorStyles = {},
    toolbarWrapperStyles = {},
    customVariableList,
    pluginsConfig,
    enableCustomVariables = false,
    placeholder = '',
    getEditorInstance = false,
    customEditorClassName = '',
    attachmentKey = 'attachments',
    attachmentFiles = DEFAULT_ATTACHMENT_FILES.join(','),
    isAutoSaveEnabled = false,
    attachmentPreviewWrapperStyles = {},
    resize = false,
    hasCodeBlock,
    onAttachmentChange,
    onAttachmentRemove,
    attachmentTooltipMessage,
    totalSizeLimitMB,
    hideToolbar = false,
    showVariablesOnRight = false,
    toolbarAddon = null,
    customExtensions = [],
    printFormat = false,
    previewProps = {
      enable: false,
      onClick: (_action) => {},
    },
    toolBarProps = {
      top: {},
      bottom: {},
      containerProps: {},
    },
    onSurveySelect,
    onSurveyRemove,
    initialSurveyId,
    enableFontSize = false,
    enableFontFamily = false,
  } = item;

  const [selectedText, setSelectedText] = useState('');
  const [selectedHtml, setSelectedHtml] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [imageConfig, setImageConfig] = useState({
    loading: false,
    disabled: false,
  });
  const [isPreview, setShowPreview] = useState(false);
  const toolBarRef = useRef(null);
  const bubbleMenuRef = useRef(null);

  const [_field, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const { setValues, values } = formik || {};

  const [, { value: attachmentValue }] = useField({ name: attachmentKey });

  const { pInlineImageSupportCount = 1 } = useKFlags();

  const [imageCount, setImageCount] = useState(0);

  const lowlight = createLowlight();
  lowlight.register({ javascript, css, html, ts });

  const isMaxImageCountExceeded =
    imageCount >= Number(pInlineImageSupportCount);
  const variablesListArr = Object.entries(customVariableList || {}).flatMap(
    ([, values = []]) => values
  );
  const [previewData, setPreviewData] = useState(null);

  const previewMutation = useMutation({
    mutationFn: getPlaceholderPreview,
    onSuccess: (data) => {
      setPreviewData(data);
      setTimeout(() => {
        addVariableHoverListeners();
      }, 0);
    },
  });

  const setEditorValue = (editor) => {
    switch (contentType) {
      case 'html':
        {
          // Convert variable atom-node spans back to `{{var}}` so formik (and
          // every send site) keeps the canonical token format.
          const val = serializeVariables(editor.getHTML());
          if (val === '<p dir="auto"></p>') setValue('');
          else setValue(val);
        }
        break;
      case 'json':
        setValue(editor.getJSON());
        break;
      case 'text':
        setValue(editor.getText());
        break;
      case 'all':
        setValue({
          text: editor.getText(),
          json: editor.getJSON(),
          html: serializeVariables(editor.getHTML()),
        });
        break;
      default:
        break;
    }
    setError(null);
  };

  const updateImageStatus = ({ html = '' }) => {
    const imgRegex = /<img[^>]+>/gi;
    const imgTags = html.match(imgRegex);
    //inline image count is set to 1 for now
    setImageCount(imgTags?.length);
    if (imgTags?.length >= Number(pInlineImageSupportCount)) {
      setImageConfig((prev) => ({ ...prev, disabled: true }));
      return;
    }
    setImageConfig((prev) => ({ ...prev, disabled: false }));
  };

  // Frontend-only replacements for dynamic values that backend can't provide,
  // like interview details during create/edit.
  const replaceDynamicPlaceholders = (htmlString, replacements) => {
    if (!htmlString) return '';
    let result = htmlString;
    for (const [key, value] of Object.entries(replacements)) {
      const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      result = result.replace(regex, value);
    }
    return result;
  };

  const addTooltipToUnsupportedVariables = (htmlString) => {
    if (!htmlString) return '';

    htmlString = replaceDynamicPlaceholders(
      htmlString,
      previewProps.previewDetails || {}
    );

    // Remove style='color:red;' if it exists
    // Don't know why we are getting color:red from BE, we've to check with Deeshath on this
    htmlString = htmlString.replace(/style=['"]?color:red;?['"]?/gi, '');

    // Replace {{anything}} with a tooltip wrapper
    const processedString = htmlString.replace(
      /(\{\{.*?\}\})/g,
      `<span class="kvariable-tooltip"><span style="background-color: #FFF; border: 1px solid #E9EBF4; padding: 2px 0; border-radius: 6px; font-weight: 450;  display: inline-block;" class="kvariable-tooltip-content">$1</span><span class="kvariable-tooltip-text kvariable-template-preview-txt" style="font-weight: 450; left: auto; right: 0px;">Data for this variable is not available yet. It will be filled in when the template is used.</span></span>`
    );

    return processedString;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: true,
        hardBreak: false,
      }),
      NonEditableTag,
      SanitizeTemplateVariables,
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: false,
        autolink: true,
        validate: (href) => {
          const urlRegex = /^(http|https):\/\/[^ "]+$/;
          return urlRegex.test(href);
        },
      }),
      Color,
      EnterHandler,
      TextStyle,
      FontSize,
      FontFamily,
      TextDirection,
      DefinitionList,
      DefinitionTerm,
      DefinitionDescription,
      SizedImage.configure({ inline: true }),
      Placeholder.configure({
        placeholder: placeholder || 'Start typing...',
      }),
      ValidateLinkOnPaste,
      ...(pluginsConfig?.includes('survey')
        ? [
            SurveyNode.configure({
              onSurveySelect: onSurveySelect,
              onSurveyRemove: onSurveyRemove,
            }),
            SurveyLinkReplacement.configure({
              initialSurveyId: initialSurveyId ?? null,
            }),
          ]
        : []),
      ...(enableCustomVariables && customVariableList
        ? [
            // Supported {{var}} → non-editable atom pill; hydration converts
            // text into nodes on load/type/paste/insert.
            VariableNode,
            VariableHydration.configure({
              supportedVariables: variablesListArr,
            }),
            // Still renders UNSUPPORTED {{var}} as editable red text + ✕.
            VariableHighlightDecoration.configure({
              enabled: enableCustomVariables,
              supportedVariables: variablesListArr,
            }),
          ]
        : []),
      ...(pluginsConfig?.includes('table')
        ? getTableExtensions({ printFormat })
        : []),
      ...(customExtensions || []),
    ].filter(Boolean), // Ensure no undefined extensions
    editable,
    content: sanitizeDom(content) || '',
    onSelectionUpdate({ editor }) {
      processSelectedContent(editor, setSelectedText, setSelectedHtml);

      const allSelected =
        editor.state.selection.from === 1 &&
        editor.state.selection.to === editor.state.doc.content.size - 1;
      setIsAllSelected(allSelected);
    },
    onUpdate: debounce(({ editor }) => {
      setEditorValue(editor);
      updateImageStatus({ html: editor.getHTML() });
      setTouched(true);
    }, 300),
    onCreate({ editor }) {
      editor?.commands?.setContent(meta?.value || content || '');
      setEditorValue(editor);
    },
  });

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    editor.setEditable(editable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  useEffect(() => {
    if (!editor) return;

    // Add focus restoration after variable insertion
    const handleVariableInsertion = () => {
      editor.commands.focus();
    };

    const handleCustomEvent = (event) => {
      if (event.detail?.type === 'kula_editor_variable_inserted') {
        handleVariableInsertion();
      }
    };

    document.addEventListener(
      'kula_editor_variable_inserted',
      handleCustomEvent
    );

    return () => {
      document.removeEventListener(
        'kula_editor_variable_inserted',
        handleCustomEvent
      );
    };
  }, [editor]);

  useEffect(() => {
    const { editorInstance } = values || {};
    if (!editorInstance && getEditorInstance && editor) {
      setValues({ ...values, editorInstance: editor });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, getEditorInstance]);

  useEffect(() => {
    if (replaceContent) {
      setValue(content);
      editor?.commands?.setContent(content || meta?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, replaceContent]);

  useEffect(() => {
    if (!editor || !customVariableList || !enableCustomVariables) return;
    editor?.commands?.updateVariablesList?.(variablesListArr);
    // Keep the atom-node hydration list in sync and re-hydrate the document.
    editor?.commands?.setHydrationVariables?.(variablesListArr);
    editor.setOptions({
      editorProps: { supportedVariables: variablesListArr },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customVariableList, editor]);

  const handleMouseOver = useCallback((event) => {
    const tooltipContainer = event.currentTarget;
    const containerBounds = tooltipContainer?.getBoundingClientRect();
    const containerLeft15Percent = (window.innerWidth * 15) / 100;
    const children = tooltipContainer?.getElementsByClassName(
      'kvariable-tooltip-text'
    );
    const variableRightLessWindowHalf =
      containerBounds.right < (window.innerWidth + containerLeft15Percent) / 2;

    if (variableRightLessWindowHalf) {
      // console.log('place in left side');
      if (children?.[0]) {
        children[0].style.position = 'fixed';
        children[0].style.top = `${containerBounds.bottom + 10}px`;
        children[0].style.left = `${
          containerBounds.left - containerLeft15Percent
        }px`;
        children[0].style.visibility = 'visible';
      }
      return;
    } else {
      // console.log('place in right side');
      if (children?.[0]) {
        children[0].classList.add('kvariable-template-preview-txt');
        children[0].style.position = 'fixed';
        children[0].style.top = `${containerBounds.bottom + 10}px`;
        children[0].style.right = `${
          window.innerWidth - containerBounds.right
        }px`;
        children[0].style.left = `inherit`;
        children[0].style.visibility = 'visible';
      }
    }
  }, []);

  const handleMouseLeave = useCallback((event) => {
    const tooltipContainer = event.currentTarget;
    const children = tooltipContainer?.getElementsByClassName(
      'kvariable-tooltip-text'
    );
    if (children?.[0]) {
      children[0].classList.remove('kvariable-template-preview-txt');
      children[0].style.visibility = 'hidden';
    }
  }, []);

  const clearHoverListerners = useCallback(() => {
    const elements = document.getElementsByClassName('kvariable-tooltip');
    for (const el of elements) {
      el.removeEventListener('mouseover', handleMouseOver);
      el.removeEventListener('mouseleave', handleMouseLeave);
    }
  }, [handleMouseOver, handleMouseLeave]);

  const addVariableHoverListeners = useCallback(() => {
    clearHoverListerners();
    setTimeout(() => {
      const elements = document.getElementsByClassName('kvariable-tooltip');
      for (const el of elements) {
        el.addEventListener('mouseover', handleMouseOver);
        el.addEventListener('mouseleave', handleMouseLeave);
      }
    }, 10);
  }, [handleMouseOver, handleMouseLeave, clearHoverListerners]);

  const renderPlugins = () => {
    if (!editable) return null;
    return (
      <Flex
        justifyContent={showVariablesOnRight ? 'flex-end' : 'flex-start'}
        className="toolbar-container"
        gap="5px"
        pt={pluginAlignment === 'bottom' && '10px'}
        {...(resize && {
          position: 'sticky',
          bottom: 0,
          top: 0,
          zIndex: 10,
          bg: 'white',
        })}
      >
        <Box
          pointerEvents={isPreview ? 'none' : 'all'}
          opacity={isPreview ? 0.5 : 1}
        >
          <EditorToolbar
            editor={editor}
            toolBarRef={toolBarRef}
            pluginsConfig={pluginsConfig}
            imageConfig={imageConfig}
            setImageConfig={setImageConfig}
            isTextSelected={selectedText?.length > 0}
            toolbarWrapperStyles={toolbarWrapperStyles}
            attachmentKey={attachmentKey}
            attachmentFiles={attachmentFiles}
            disableConfig={item?.disableConfig}
            isMaxImageCountExceeded={isMaxImageCountExceeded}
            hasCodeBlock={hasCodeBlock}
            onAttachmentChange={onAttachmentChange}
            onAttachmentRemove={onAttachmentRemove}
            attachmentTooltipMessage={attachmentTooltipMessage}
            totalSizeLimitMB={totalSizeLimitMB}
            hideToolbar={hideToolbar}
            onSurveySelect={onSurveySelect}
            onSurveyRemove={onSurveyRemove}
            enableFontSize={enableFontSize}
            enableFontFamily={enableFontFamily}
          />
        </Box>
        {toolbarAddon && (
          <Box
            pointerEvents={isPreview ? 'none' : 'all'}
            opacity={isPreview ? 0.5 : 1}
          >
            {toolbarAddon}
          </Box>
        )}
        {enableCustomVariables && (
          <Flex gap="15px" mr="12px">
            <Box
              pointerEvents={isPreview ? 'none' : 'all'}
              opacity={isPreview ? 0.5 : 1}
            >
              <EditorCustomVariables
                config={customVariableList}
                editor={editor}
              />
            </Box>
            {previewProps?.enable && (
              <Button
                leftIcon={<Icon as={isPreview ? EyeOff : Eye} />}
                variant={isPreview ? 'tertiary' : 'quaternary'}
                isLoading={previewMutation.isPending}
                onClick={() => {
                  if (isPreview) setShowPreview(false);
                  else {
                    setShowPreview(true);
                    const previewPayload = {
                      ...{
                        body: meta?.value || content,
                        retain_variables: true,
                      },
                      ...previewProps.payload,
                    };
                    previewMutation.mutate(previewPayload);
                  }
                  previewProps?.onClick?.(!isPreview);
                }}
              >
                Preview
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    );
  };

  if (isPreview) {
    const htmlString = addTooltipToUnsupportedVariables(previewData?.body);
    return (
      <Box
        className={`editor-style ${customEditorClassName} `}
        borderRadius="8px"
        border="1px solid"
        borderColor="border-color"
        overflow="hidden"
        position={isAllSelected && 'relative'}
        {...editorContainerStyles}
        {...(resize && { resize: 'vertical', minHeight: '250px' })}
        bg="secondary-25"
        mx="-24px"
        px="24px"
      >
        <Flex
          flexDirection="column"
          h="100%"
          padding="10px"
          position="relative"
          {...editorWraperStyles}
        >
          {pluginAlignment === 'top' && renderPlugins()}
          <Box
            className="editor-style"
            style={{
              minHeight: '250px',
              padding: '10px',
              paddingBottom: pluginAlignment === 'bottom' ? '50px' : '0px',
              overflowY: 'auto',
              transition: 'height 1s ease',
              position: !isAllSelected && 'relative',
              ...editorStyles,
            }}
          >
            <SanitizedBox
              fontSize="14px"
              html={htmlString}
              color="secondary-700"
            />
          </Box>
          {pluginAlignment === 'bottom' && renderPlugins()}
        </Flex>
      </Box>
    );
  }

  return (
    <>
      <Box bg="#fff" zIndex={2} {...toolBarProps.top}>
        <Box paddingTop={'10px'} {...toolBarProps.containerProps}>
          {pluginAlignment === 'top' && renderPlugins()}
        </Box>
      </Box>
      <Box
        className={`editor-style ${customEditorClassName} `}
        borderRadius="8px"
        border="1px solid"
        borderColor={
          meta?.touched && meta?.error ? 'error-200' : 'border-color'
        }
        overflow="hidden"
        position={isAllSelected && 'relative'}
        onClick={() => {
          editor?.commands?.focus();
        }}
        {...editorContainerStyles}
        {...(resize && { resize: 'vertical', minHeight: '250px' })}
      >
        <Flex
          flexDirection="column"
          h="100%"
          padding="10px"
          position="relative"
          {...editorWraperStyles}
        >
          <Box
            className="editor-style"
            style={{
              minHeight: '250px',
              padding: '10px',
              paddingBottom: pluginAlignment === 'bottom' ? '50px' : '0px',
              overflowY: 'auto',
              flex: 1,
              transition: 'height 1s ease',
              position: !isAllSelected && 'relative',
              ...editorStyles,
            }}
          >
            {renderAdditionalContent({
              editor,
              selectedText,
              selectedHtml,
              bubbleMenuRef,
            })}
            {imageConfig?.loading && (
              <Center
                pos="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="#F2F4F7"
                opacity="0.5"
                zIndex="1"
              >
                <Spinner color="primary-800" />
              </Center>
            )}
            <EditorContent
              editor={editor}
              id={name}
              onBlur={() => setTouched(true)}
            />
            {pluginsConfig?.includes('table') && (
              <TableBubbleMenu editor={editor} />
            )}
          </Box>
          {pluginsConfig?.includes('attachment') &&
            attachmentValue?.length > 0 && (
              <Box
                className="attachment-preview-container"
                overflow="hidden"
                pb="20px"
              >
                <Flex
                  gap="10px"
                  p="20px 0"
                  h="fit-content"
                  overflowX="auto"
                  overflowY="hidden"
                  className="attachment-preview-wrapper"
                  {...attachmentPreviewWrapperStyles}
                >
                  {attachmentValue?.map((attachment, idx) => (
                    <FileAttachmentPreview
                      key={idx}
                      file={attachment}
                      bg="ats-bg-primary"
                      attachmentKey={attachmentKey}
                      onAttachmentRemove={onAttachmentRemove}
                    />
                  ))}
                </Flex>
              </Box>
            )}
          {pluginAlignment === 'bottom' && renderPlugins()}
        </Flex>
        {isAutoSaveEnabled && (
          <AutoSave
            data={formik?.values?.[item.name]}
            saveData={(data) => formik.setFieldValue(item.name, data)}
            onDebouncedValueChange={item.handleDebouncedValueChange}
          />
        )}
      </Box>
    </>
  );
};
